// A click on a locked object in the editor doesn't select it: the object
// flashes red here (LockedFlash, inside the <Canvas>) and a short notice
// points to the Scene Objects list, where it can be unlocked (LockedNotice).
import React, { useEffect, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Lock } from 'lucide-react';
import { useWorldStore } from '../stores/worldStore';

const FLASH_MS = 1300;

function findObject(scene: THREE.Scene, id: string): THREE.Object3D | null {
  let hit: THREE.Object3D | null = null;
  scene.traverse((o) => {
    if (!hit && (o.userData?.objectId === id || o.name === `primitive-${id}`)) hit = o;
  });
  return hit;
}

export const LockedFlash: React.FC = () => {
  const scene = useThree((s) => s.scene);
  const flash = useWorldStore((s) => s.lockFlash);
  const fx = useRef<{ group: THREE.Group; mats: THREE.Material[]; start: number } | null>(null);

  useEffect(() => {
    if (!flash) return;
    const target = findObject(scene, flash.id);
    if (!target) return;
    const box = new THREE.Box3().setFromObject(target);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3()).addScalar(0.04);
    const center = box.getCenter(new THREE.Vector3());
    const fill = new THREE.MeshBasicMaterial({ color: '#ff2a2a', transparent: true, opacity: 0.6, depthTest: false, depthWrite: false });
    const edge = new THREE.LineBasicMaterial({ color: '#ff4d4d', transparent: true, opacity: 1, depthTest: false });
    const geo = new THREE.BoxGeometry(size.x, size.y, size.z);
    const group = new THREE.Group();
    group.position.copy(center);
    const mesh = new THREE.Mesh(geo, fill); mesh.renderOrder = 999; mesh.raycast = () => {};
    const lines = new THREE.LineSegments(new THREE.EdgesGeometry(geo), edge); lines.renderOrder = 1000; lines.raycast = () => {};
    group.add(mesh, lines);
    scene.add(group);
    fx.current = { group, mats: [fill, edge], start: performance.now() };
    return () => {
      scene.remove(group);
      geo.dispose(); fill.dispose(); edge.dispose();
      if (fx.current?.group === group) fx.current = null;
    };
  }, [flash, scene]);

  useFrame(() => {
    const f = fx.current;
    if (!f) return;
    const t = (performance.now() - f.start) / FLASH_MS;
    if (t >= 1) { f.group.visible = false; return; }
    // two strong pulses, then fade
    const pulse = 0.55 + 0.45 * Math.cos(t * Math.PI * 4);
    const fade = t < 0.75 ? 1 : 1 - (t - 0.75) / 0.25;
    (f.mats[0] as THREE.MeshBasicMaterial).opacity = 0.6 * pulse * fade;
    (f.mats[1] as THREE.LineBasicMaterial).opacity = fade;
  });
  return null;
};

export const LockedNotice: React.FC = () => {
  const flash = useWorldStore((s) => s.lockFlash);
  const name = useWorldStore((s) => {
    const id = s.lockFlash?.id;
    if (!id) return '';
    return s.primitives[id]?.name || s.lights[id]?.name || s.contentItems[id]?.filename || (s.screenProps[id] ? 'Screen' : '') || 'This object';
  });
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!flash) return;
    setShow(true);
    const t = setTimeout(() => setShow(false), 2600);
    return () => clearTimeout(t);
  }, [flash]);
  if (!show) return null;
  return (
    <div className="gx-locked-notice" role="status">
      <Lock size={14} /> <strong>{name}</strong> is locked. Unlock it in Scene Objects to select or move it.
    </div>
  );
};
